// Auto-structured from the official Jejak Semangat privacy notice (English).
// Inline **text** marks bold; e-mail addresses and URLs are linked by the renderer.
const privacyNoticeEn = {
  "lang": "en",
  "label": "English",
  "title": "Jejak Semangat Sdn Bhd’s Privacy Notice",
  "intro": [
    {
      "type": "p",
      "text": "Jejak Semangat Sdn Bhd (\"Jejak Semangat\", \"we\", \"us\" or \"our\") respects your privacy and is committed to protecting your Personal Data in accordance with the Personal Data Protection Act 2010 (Act 709) (\"PDPA\"), including the Personal Data Protection (Amendment) Act 2024, and other applicable laws and regulations in Malaysia."
    },
    {
      "type": "p",
      "text": "This Privacy Notice explains how Jejak Semangat collects, records, stores, uses, processes, discloses and protects your Personal Data when you access or use our mobile application, purchase or subscribe to our telecommunications products and services, register for a SIM card, interact with our representatives, or otherwise engage with us."
    },
    {
      "type": "p",
      "text": "We are committed to handling your Personal Data responsibly, transparently and securely. We process Personal Data only for legitimate purposes connected with our business operations and the provision of our products and services, subject to applicable legal requirements."
    },
    {
      "type": "p",
      "text": "By providing your Personal Data to us or using our products and services, you acknowledge that you have been informed of the matters described in this Privacy Notice. Where consent is required under applicable law, we will obtain your consent through the appropriate means before carrying out the relevant processing activities."
    }
  ],
  "sections": [
    {
      "title": "Scope of Privacy Notice",
      "blocks": [
        {
          "type": "p",
          "text": "This Privacy Notice applies to all individuals whose Personal Data is collected or processed by Jejak Semangat in connection with our business activities, including:"
        },
        {
          "type": "ul",
          "items": [
            "Existing and prospective customers or subscribers",
            "Individuals registering for or purchasing Jejak Semangat SIM cards and telecommunications packages",
            "Foreign workers and other individuals using our mobile telecommunications services",
            "Individuals accessing or using our mobile application, website or other digital platforms",
            "Individuals communicating with our customer service representatives or authorized sales agents",
            "Other individuals whose Personal Data is provided to us in connection with our products, services or business operations"
          ]
        },
        {
          "type": "p",
          "text": "This Privacy Notice applies to Personal Data collected through our mobile application, website, registration channels, sales representatives, customer service channels, business partners and other relevant sources."
        },
        {
          "type": "p",
          "text": "This Privacy Notice should be read together with any additional notices or terms that may be provided to you in connection with specific products, services or activities."
        }
      ],
      "id": "section-1"
    },
    {
      "title": "Personal Data Collected",
      "blocks": [
        {
          "type": "p",
          "text": "Depending on the nature of your interaction with us and the products or services you use, Jejak Semangat may collect and process the following categories of Personal Data:"
        },
        {
          "type": "h",
          "text": "A. Personal Identification Information"
        },
        {
          "type": "ul",
          "items": [
            "Full name;",
            "Passport number and passport details;",
            "Nationality;",
            "Date of birth;",
            "Gender;",
            "Passport photographs or copies of identification documents, where required;",
            "Other information necessary for subscriber identification and verification."
          ]
        },
        {
          "type": "h",
          "text": "B. Contact and Residential Information"
        },
        {
          "type": "ul",
          "items": [
            "Mobile telephone number;",
            "Residential or correspondence address;",
            "Email address;",
            "Emergency contact information, where applicable."
          ]
        },
        {
          "type": "h",
          "text": "C. Employment and Immigration Information"
        },
        {
          "type": "ul",
          "items": [
            "Employer or company name, where applicable;",
            "Employment-related particulars;",
            "Work permit details;",
            "Relevant immigration documentation and information required for subscriber registration or verification."
          ]
        },
        {
          "type": "h",
          "text": "D. Subscriber and Telecommunications Information"
        },
        {
          "type": "ul",
          "items": [
            "SIM card serial number;",
            "Mobile Subscriber Integrated Services Digital Network Number (MSISDN);",
            "Subscriber registration information and status;",
            "SIM activation and deactivation records;",
            "Mobile package and subscription information;",
            "Package purchase, renewal and activation records;",
            "Telecommunications service usage information and related technical records, where made available to or processed by us."
          ]
        },
        {
          "type": "h",
          "text": "E. Payment and Transaction Information"
        },
        {
          "type": "ul",
          "items": [
            "Package purchase history;",
            "Payment transaction details;",
            "Transaction reference numbers;",
            "Payment amounts and payment status;",
            "Refund, adjustment and payment-related records."
          ]
        },
        {
          "type": "h",
          "text": "F. Technical and Digital Information"
        },
        {
          "type": "ul",
          "items": [
            "Internet Protocol (IP) address;",
            "Device type and device identifiers;",
            "Mobile application usage information;",
            "Application access and technical logs;",
            "Cookies and similar technologies, where applicable;",
            "Other technical information generated through your use of our digital platforms."
          ]
        },
        {
          "type": "h",
          "text": "G. Communication and Customer Service Information"
        },
        {
          "type": "ul",
          "items": [
            "Customer enquiries and complaints;",
            "Correspondence and communication records;",
            "Customer feedback;",
            "Records of interactions with our representatives and customer service personnel."
          ]
        },
        {
          "type": "p",
          "text": "We may also collect other information where it is necessary for the provision of our products and services or required by applicable laws and regulations."
        },
        {
          "type": "p",
          "text": "Where we collect Sensitive Personal Data, we will process such information only where permitted under applicable law and subject to the relevant legal requirements."
        }
      ],
      "id": "section-2"
    },
    {
      "title": "Source(s) of Personal Data",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat may obtain your Personal Data from various sources, including:"
        },
        {
          "type": "ol",
          "items": [
            "**Directly from you:** When you register for a SIM card, subscribe to a telecommunications package, submit information through our mobile application, make a payment, contact our customer service representatives or otherwise provide information to us.",
            "**Through our authorized representatives or sales channels:** When you register or purchase our products and services through our sales representatives, agents or other authorized channels.",
            "**From Telecommunications Service Providers:** Where relevant service providers provide registration responses, subscriber verification results, MSISDN approval status, package activation results, service information or other relevant information in connection with the telecommunications services provided to you.",
            "**From payment service providers and financial institutions:** Where necessary to process, verify and reconcile payments or transactions made in connection with our products and services.",
            "**From third-party service providers and business partners:** Where such information is provided in connection with services or activities involving Jejak Semangat.",
            "**From publicly available sources or relevant authorities:** Where permitted by applicable laws and necessary for verification, compliance or other legitimate business purposes."
          ]
        }
      ],
      "id": "section-3"
    },
    {
      "title": "Purposes of Collecting and Processing Personal Data",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat collects and processes your Personal Data for the following purposes:"
        },
        {
          "type": "h",
          "text": "A. Subscriber Registration and Verification"
        },
        {
          "type": "ul",
          "items": [
            "To register customers and facilitate SIM card subscriptions;",
            "To verify customer identity and validate registration information;",
            "To collect and submit relevant subscriber information to the relevant service providers for registration processing and approval;",
            "To receive subscriber registration approval or rejection responses from the relevant service providers;",
            "To maintain and update approved subscriber and MSISDN records within our systems;",
            "To facilitate SIM card issuance, activation and subscriber account administration."
          ]
        },
        {
          "type": "h",
          "text": "B. Telecommunications Services and Package Management"
        },
        {
          "type": "ul",
          "items": [
            "To provide mobile telecommunications services and SIM packages;",
            "To facilitate the purchase, subscription, renewal and activation of mobile packages;",
            "To transmit package activation requests and relevant subscriber information to the relevant service providers through integrated systems and application programming interfaces (APIs).",
            "To manage customer subscriptions, package status and service-related enquiries;",
            "To communicate information relating to service availability, package changes and service updates."
          ]
        },
        {
          "type": "h",
          "text": "C. Payment and Transaction Management"
        },
        {
          "type": "ul",
          "items": [
            "To process payments for SIM cards, telecommunications packages and related services;",
            "To maintain transaction and payment records;",
            "To verify payment status and reconcile transactions;",
            "To manage refunds, adjustments and payment-related enquiries."
          ]
        },
        {
          "type": "h",
          "text": "D. Customer Service and Communications"
        },
        {
          "type": "ul",
          "items": [
            "To respond to customer enquiries, complaints and requests;",
            "To provide technical assistance and customer support;",
            "To communicate registration outcomes, payment confirmations and subscription information;",
            "To notify customers of relevant service announcements, changes and updates."
          ]
        },
        {
          "type": "h",
          "text": "E. Business Operations and Administration"
        },
        {
          "type": "ul",
          "items": [
            "To maintain customer databases and business records;",
            "To conduct internal administration, reporting and auditing;",
            "To monitor and improve our products, services and digital platforms;",
            "To analyze service performance and customer feedback;",
            "To maintain the security and integrity of our business systems."
          ]
        },
        {
          "type": "h",
          "text": "F. Security, Fraud Prevention and Legal Compliance"
        },
        {
          "type": "ul",
          "items": [
            "To detect, investigate and prevent fraudulent, unauthorized or suspicious activities;",
            "To protect our customers, business operations and systems;",
            "To comply with applicable laws, regulations, regulatory requirements and lawful requests from relevant authorities;",
            "To establish, exercise or defend legal rights and claims."
          ]
        },
        {
          "type": "h",
          "text": "G. Marketing and Promotions"
        },
        {
          "type": "ul",
          "items": [
            "To provide information about our products, services, promotions and special offers;",
            "To conduct customer engagement and marketing activities, where permitted by applicable laws;",
            "To improve the relevance of our products and promotional communications based on customer preferences, where applicable."
          ]
        },
        {
          "type": "p",
          "text": "We will not use your Personal Data for purposes materially different from those stated above without providing appropriate notice and obtaining consent where required by applicable law."
        }
      ],
      "id": "section-4"
    },
    {
      "title": "Disclosure of Personal Data to Third Parties",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat may disclose your Personal Data to relevant third parties where necessary for the purposes described in this Privacy Notice, subject to applicable laws and regulations."
        },
        {
          "type": "p",
          "text": "Such third parties may include:"
        },
        {
          "type": "h",
          "text": "A. Telecommunications Service Providers"
        },
        {
          "type": "p",
          "text": "As part of our business operations, we may disclose relevant Personal Data to telecommunications service providers and their authorized representatives for purposes including subscriber registration, identity verification, registration approval, SIM activation, mobile package activation, telecommunications service delivery, network operations and related regulatory or operational requirements."
        },
        {
          "type": "h",
          "text": "B. Payment Service Providers and Financial Institutions"
        },
        {
          "type": "p",
          "text": "Relevant payment information may be disclosed to banks, payment gateways, payment service providers and financial institutions for payment processing, verification, settlement, reconciliation and refund purposes."
        },
        {
          "type": "h",
          "text": "C. Technology and Service Providers"
        },
        {
          "type": "p",
          "text": "We may disclose relevant Personal Data to third-party service providers supporting our business operations, including mobile application developers, cloud hosting providers, IT infrastructure providers, system maintenance providers, data storage providers and technical support providers."
        },
        {
          "type": "h",
          "text": "D. Business Partners and Authorized Representatives"
        },
        {
          "type": "p",
          "text": "We may disclose relevant information to our authorized agents, representatives, distributors and business partners where necessary to facilitate customer registration, product sales, customer service and related business activities."
        },
        {
          "type": "h",
          "text": "E. Professional Advisers and Auditors"
        },
        {
          "type": "p",
          "text": "Where necessary, we may disclose relevant Personal Data to our lawyers, accountants, auditors, consultants and other professional advisers for legitimate business, legal, compliance or auditing purposes."
        },
        {
          "type": "h",
          "text": "F. Regulatory Authorities and Government Agencies"
        },
        {
          "type": "p",
          "text": "We may disclose Personal Data to relevant regulatory authorities, government agencies, law enforcement agencies or other authorized parties where required or permitted under applicable laws, regulations, court orders or lawful requests."
        },
        {
          "type": "h",
          "text": "G. Other Relevant Parties"
        },
        {
          "type": "p",
          "text": "We may disclose Personal Data to other parties where necessary for the purposes for which the information was collected, with your consent where required, or where otherwise permitted by applicable laws."
        },
        {
          "type": "p",
          "text": "We take reasonable steps to ensure that third parties receiving Personal Data from us are subject to appropriate confidentiality, security and data protection obligations, having regard to the nature of the information and the services provided."
        }
      ],
      "id": "section-5"
    },
    {
      "title": "Consent and Choice",
      "blocks": [
        {
          "type": "p",
          "text": "By providing your Personal Data to Jejak Semangat, you acknowledge that you have been informed of the purposes for which your Personal Data may be collected, processed and disclosed as described in this Privacy Notice."
        },
        {
          "type": "p",
          "text": "Where required under applicable laws, we will obtain your consent before collecting, using or disclosing your Personal Data for the relevant purposes."
        },
        {
          "type": "p",
          "text": "You may choose not to provide certain Personal Data. However, please note that some information is necessary for us to register you as a subscriber, verify your identity, process your SIM card registration, facilitate package activation and provide the requested telecommunications services."
        },
        {
          "type": "p",
          "text": "Where you do not provide information necessary for these purposes, we may be unable to complete your registration, obtain the necessary subscriber verification and approval, or provide the relevant products and services to you."
        },
        {
          "type": "p",
          "text": "You may withdraw your consent to the processing of your Personal Data by submitting a written request to us through the contact details provided in this Privacy Notice. Any withdrawal of consent will be subject to applicable legal requirements and contractual obligations."
        },
        {
          "type": "p",
          "text": "Please note that withdrawal of consent may affect our ability to continue providing certain products or services to you. We may also continue to retain or process certain Personal Data where required or permitted by applicable laws."
        },
        {
          "type": "h2",
          "text": "Mandatory and Voluntary Information"
        },
        {
          "type": "p",
          "text": "Certain Personal Data is mandatory for the purposes of subscriber registration, identity verification, SIM card activation, payment processing and the provision of our telecommunications services. You will be informed where the provision of such information is required."
        },
        {
          "type": "p",
          "text": "Other information may be provided voluntarily, such as additional contact details or customer feedback, where applicable."
        },
        {
          "type": "p",
          "text": "If you do not provide Personal Data that is necessary for the relevant purposes, we may be unable to process your registration, activate your SIM card or provide the requested products and services."
        }
      ],
      "id": "section-6"
    },
    {
      "title": "Security of Personal Data",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat takes the protection of your Personal Data seriously and will take reasonable practical steps to protect Personal Data against loss, misuse, modification, unauthorized or accidental access or disclosure, alteration or destruction."
        },
        {
          "type": "p",
          "text": "These measures may include appropriate administrative, organisational, physical and technical safeguards, such as:"
        },
        {
          "type": "ul",
          "items": [
            "Restricting access to Personal Data to authorized personnel who require such access for legitimate business purposes;",
            "Implementing appropriate access controls and authentication measures;",
            "Maintaining reasonable security measures for our mobile application, databases and IT systems;",
            "Protecting information transmitted between our systems and relevant service providers through appropriate security measures;",
            "Requiring relevant employees, representatives and service providers to observe confidentiality obligations;",
            "Reviewing and improving our data protection and information security practices where necessary."
          ]
        },
        {
          "type": "p",
          "text": "Where Personal Data is processed by third-party service providers, telecommunications partners or other authorized parties in connection with the provision of our products and services, we will take reasonable steps to ensure that appropriate safeguards and data protection obligations are in place, having regard to the respective roles and responsibilities of the parties involved."
        },
        {
          "type": "p",
          "text": "Notwithstanding the measures taken, no method of electronic transmission or data storage can be guaranteed to be completely secure. We will continue to take reasonable steps to safeguard your Personal Data in accordance with applicable legal requirements."
        }
      ],
      "id": "section-7"
    },
    {
      "title": "Retention of Personal Data",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat will retain your Personal Data only for as long as necessary to fulfil the purposes for which it was collected, including the provision of our products and services, customer account administration, transaction management, regulatory compliance and other legitimate business purposes."
        },
        {
          "type": "p",
          "text": "The retention period may vary depending on the nature of the Personal Data, the purpose of processing, the duration of your subscription or business relationship with us, and any applicable legal or regulatory requirements."
        },
        {
          "type": "p",
          "text": "Upon expiry of the relevant retention period, or when the Personal Data is no longer required for the purposes for which it was collected, we will take reasonable steps to securely delete, destroy or permanently anonymise the information, unless further retention is required or permitted under applicable laws."
        },
        {
          "type": "p",
          "text": "Please note that certain subscriber, registration, transaction or telecommunications records may also be retained by relevant telecommunications service providers in accordance with their respective legal, regulatory and operational requirements."
        }
      ],
      "id": "section-8"
    },
    {
      "title": "Cross-Border Transfer of Personal Data",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat may engage third-party service providers or technology providers that store, host or process Personal Data outside Malaysia."
        },
        {
          "type": "p",
          "text": "Where Personal Data is transferred to a place outside Malaysia, we will ensure that such transfer is carried out in accordance with Section 129 of the Personal Data Protection Act 2010 and applicable cross-border data transfer requirements."
        },
        {
          "type": "p",
          "text": "Where necessary, we will take reasonable steps to assess the level of protection afforded to Personal Data in the destination country and ensure that appropriate safeguards or applicable legal conditions are satisfied before transferring Personal Data."
        },
        {
          "type": "p",
          "text": "Personal Data may also be shared with relevant telecommunications service providers and authorized third parties where necessary for the provision of our products and services, subject to applicable legal requirements and safeguards governing the transfer and processing of Personal Data."
        }
      ],
      "id": "section-9"
    },
    {
      "title": "Cookies and Mobile Application Data",
      "blocks": [
        {
          "type": "p",
          "text": "Where applicable, our website and mobile application may use cookies, software development tools, application logs and similar technologies to collect certain technical information."
        },
        {
          "type": "p",
          "text": "Such information may include:"
        },
        {
          "type": "ul",
          "items": [
            "IP addresses;",
            "Device type and operating system;",
            "Application version;",
            "Login and access timestamps;",
            "Application usage and interaction records;",
            "Technical and diagnostic information."
          ]
        },
        {
          "type": "p",
          "text": "We may use this information to:"
        },
        {
          "type": "ol",
          "items": [
            "Maintain and improve the performance of our website and mobile application;",
            "Identify and resolve technical issues;",
            "Maintain application security and prevent unauthorized access;",
            "Understand general usage patterns and improve user experience; and",
            "Support system administration and operational monitoring."
          ]
        },
        {
          "type": "p",
          "text": "You may manage certain cookies and application permissions through your browser or device settings. However, disabling certain technologies or permissions may affect the availability or functionality of some services."
        },
        {
          "type": "p",
          "text": "Where third-party analytics or similar technologies are used, their collection and processing of information may also be subject to the relevant third party's privacy policies."
        }
      ],
      "id": "section-10"
    },
    {
      "title": "Direct Marketing",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat may use your contact information to provide you with updates, promotional materials, offers and information relating to our products and services, where permitted under applicable laws."
        },
        {
          "type": "p",
          "text": "Such communications may be provided through SMS, telephone calls, email, messaging applications, mobile application notifications or other communication channels."
        },
        {
          "type": "p",
          "text": "We respect your preferences regarding marketing communications. If you no longer wish to receive promotional or marketing messages from us, you may request to opt out by contacting us through the contact details provided in this Privacy Notice or by using any unsubscribe or opt-out facility made available in the relevant communication."
        },
        {
          "type": "p",
          "text": "Please note that opting out of marketing communications will not prevent us from sending essential service-related notifications, including registration updates, payment confirmations, security notifications and important service announcements."
        }
      ],
      "id": "section-11"
    },
    {
      "title": "Your Rights Under the PDPA",
      "blocks": [
        {
          "type": "p",
          "text": "Subject to the Personal Data Protection Act 2010 and other applicable laws, you may exercise the following rights in relation to your Personal Data:"
        },
        {
          "type": "h",
          "text": "A. Right to Access"
        },
        {
          "type": "p",
          "text": "You may request access to your Personal Data held by Jejak Semangat, subject to applicable legal requirements and verification procedures."
        },
        {
          "type": "h",
          "text": "B. Right to Correction"
        },
        {
          "type": "p",
          "text": "You may request the correction or updating of Personal Data that is inaccurate, incomplete, misleading or outdated."
        },
        {
          "type": "h",
          "text": "C. Right to Withdraw Consent"
        },
        {
          "type": "p",
          "text": "Where processing is based on your consent, you may withdraw your consent by providing written notice to us, subject to applicable legal requirements and any consequences arising from such withdrawal."
        },
        {
          "type": "h",
          "text": "D. Right to Prevent Processing for Direct Marketing"
        },
        {
          "type": "p",
          "text": "You may request that we stop processing your Personal Data for direct marketing purposes."
        },
        {
          "type": "h",
          "text": "E. Right to Prevent Processing Likely to Cause Damage or Distress"
        },
        {
          "type": "p",
          "text": "You may exercise your applicable rights to require us to cease or not begin processing Personal Data where the processing is likely to cause substantial damage or substantial distress, in accordance with the PDPA."
        },
        {
          "type": "h",
          "text": "F. Right to Data Portability"
        },
        {
          "type": "p",
          "text": "Where applicable, you may request that your Personal Data be transmitted directly to another data controller of your choice, subject to the technical feasibility and compatibility of the relevant data format and other applicable legal requirements."
        },
        {
          "type": "p",
          "text": "To exercise any of these rights, please submit a written request through the contact details provided in this Privacy Notice."
        },
        {
          "type": "p",
          "text": "We may request additional information to verify your identity before processing your request. We will respond to your request in accordance with applicable laws and regulations."
        },
        {
          "type": "p",
          "text": "Please note that certain requests may be subject to legal exemptions, limitations or circumstances where we are permitted to refuse the request under applicable laws."
        }
      ],
      "id": "section-12"
    },
    {
      "title": "Privacy and Data Protection Contact",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat is committed to maintaining appropriate accountability and oversight of its Personal Data processing activities."
        },
        {
          "type": "p",
          "text": "For enquiries, requests or complaints relating to the collection, processing or protection of your Personal Data, you may contact us through the following channels:"
        },
        {
          "type": "h2",
          "text": "Privacy Enquiries / Data Protection Contact"
        },
        {
          "type": "p",
          "text": "Email: compliance@mobilityone.com.my"
        },
        {
          "type": "p",
          "text": "Telephone: +603 9213 0688"
        },
        {
          "type": "p",
          "text": "Address: Wisma LMS, No. 6, Jalan Abdul Rahman Idris, Kg Baru, 50300 Kuala Lumpur, Malaysia."
        },
        {
          "type": "p",
          "text": "Where required under applicable laws and regulatory guidelines, Jejak Semangat will appoint and register a Data Protection Officer (DPO) to oversee relevant personal data protection matters."
        }
      ],
      "id": "section-13"
    },
    {
      "title": "Contact Us",
      "blocks": [
        {
          "type": "p",
          "text": "If you have any questions, concerns, requests or complaints regarding this Privacy Notice or the handling of your Personal Data by Jejak Semangat, please contact us through the following channels:"
        },
        {
          "type": "h",
          "text": "JEJAK SEMANGAT SDN BHD"
        },
        {
          "type": "p",
          "text": "Registered Address: Wisma LMS, No.6 Jalan Abdul Rahman Idris, Kg Baru, 50300, Kuala Lumpur, Malaysia"
        },
        {
          "type": "p",
          "text": "Email: enquiry@tfp.com.my"
        },
        {
          "type": "p",
          "text": "Website: https://www.tfp.com.my/"
        },
        {
          "type": "p",
          "text": "Contact: https://linktr.ee/tfpsolutionsofficial"
        },
        {
          "type": "p",
          "text": "We will make reasonable efforts to address your enquiries and concerns in a timely manner and in accordance with applicable laws and regulations."
        }
      ],
      "id": "section-14"
    },
    {
      "title": "Changes to This Privacy Notice",
      "blocks": [
        {
          "type": "p",
          "text": "Jejak Semangat reserves the right to amend, update or revise this Privacy Notice from time to time to reflect changes in our business operations, products and services, technological developments, legal requirements or regulatory obligations."
        },
        {
          "type": "p",
          "text": "Any updated version of this Privacy Notice will be published through our official website, mobile application or other appropriate communication channels."
        },
        {
          "type": "p",
          "text": "Where required, we will notify you of material changes to this Privacy Notice."
        },
        {
          "type": "p",
          "text": "We encourage you to review this Privacy Notice periodically to remain informed of how we collect, use, process and protect your Personal Data."
        },
        {
          "type": "p",
          "text": "The latest version of this Privacy Notice will supersede all previous versions."
        }
      ],
      "id": "section-15"
    }
  ],
  "lastUpdated": "Last Updated: 2 November 2026"
};

export default privacyNoticeEn;
